import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {MeasurementDataset} from "../base-measurement-chart";
import {DashboardContext} from "../../dashboard/dashboard.context";
import {ChartDataProvider, DashboardTime} from "../../../utils/chart-data-provider";
import moment from "moment";
import lodash from "lodash";
import {ChartCompare} from "../../dashboard/dashboard.types";
import {trackByIndex} from "../../../common/utils";
import {View} from "../../../common/view";
import {Handler} from "../../../common/handler";
import {DataQueryFilters} from "../../measurements-component/measurements-handler.component";
import {ChartUtilsService} from "../chart-utils.service";
import {Measurements} from "../../measurements-component/measurements";

@Component({
    selector: 'app-chart-table',
    templateUrl: './chart-table.component.html',
    styleUrls: ['./chart-table.component.scss']
})
@Handler()
export class ChartTableComponent extends View implements OnInit, AfterViewInit {
    protected readonly trackByIndex = trackByIndex;
    context = DashboardContext;
    @Input() dataProvider: ChartDataProvider;
    @Input() compare: ChartCompare;
    _data: MeasurementDataset[];
    headerTop: string;
    calculatedColumnTimes: ColumnTime[] = [];

    currentPeriod: MeasurementDataset[] = [];
    previousComparePeriod: MeasurementDataset[] = [];
    filters: DataQueryFilters;

    @ViewChild("table") table: ElementRef;
    @Output() tableElement: EventEmitter<ElementRef> = new EventEmitter<ElementRef>();

    private _splitOffPeak = false;

    @Input()
    set splitOffPeak(value: boolean) {
        this._splitOffPeak = value ?? false;
    }

    ngOnInit(): void {
        this.subscribeTo("getChartDataQueryFilters").subscribe((f: DataQueryFilters) => this.filters = f);
        this.dataProvider.aggregatedDataChanged.subscribe(d => {
            this._data = d.datasets.filter(d => !d.dataset.hidden);
            this.calculatedColumnTimes = this.columnTimes();
            this.currentPeriod = this._data
                .sort((a, b) => a.dataset.order < b.dataset.order ? -1 : 1)
                .filter(r => r.dataset.stack === DashboardContext.stacks.currentPeriod
                    && r.dataset.yAxisID !== "yScatter");
            this.previousComparePeriod = this._data
                .sort((a, b) => a.dataset.order < b.dataset.order ? -1 : 1)
                .filter(r => r.dataset.stack === DashboardContext.stacks.lastYear);
            setTimeout(() => this.headerTop = this.getHeaderTop() + "px", 10);
        });
    }

    ngAfterViewInit() {
        setTimeout(() => this.headerTop = this.getHeaderTop() + "px", 1000);
        this.tableElement.emit(this.table);
    }

    records(dataset: MeasurementDataset[], previousDatasets: MeasurementDataset[]): Data[] {
        const slots = this.rows();
        return slots.map((r, i) => {
            let periodChange = false;
            if (slots[i - 1]) {
                periodChange = moment(slots[i - 1].startTime).year() < moment(r.startTime).year()
            }
            return {
                label: r.tableHeader,
                periodChange: periodChange,
                data: dataset
                    .map((d, j) => {
                        const prevDataset = previousDatasets.find(ds => ds.entityId === d.entityId && ds.measurementType == d.measurementType);
                        const val: number = (d.dataset.tooltip.valuesInverted ?
                            (-(d.dataset.tooltip?.data?.[i] ?? d.dataset.data[i])) :
                            (d.dataset.tooltip?.data?.[i] ?? d.dataset.data[i])) as number;
                        const prevVal: number = (prevDataset
                            ? (prevDataset.dataset.tooltip.valuesInverted
                                ? (-(prevDataset.dataset.tooltip?.data?.[i] ?? prevDataset.dataset.data[i]))
                                : (prevDataset.dataset.tooltip?.data?.[i] ?? prevDataset.dataset.data[i]))
                            : 0) as number;
                        let diffPercentage = lodash.round(((val - prevVal) / prevVal) * 100, 1);
                        const percentage = lodash.round(diffPercentage, 1);
                        return <Record>{
                            val: val,
                            prevVal: prevVal,
                            percentage: percentage,
                            finite: lodash.isFinite(diffPercentage) && val !== 0,
                            cssClass: percentage > 0
                                ? (d.dataset.tooltip.valuesInverted || d.dataset.tooltip.higherValueIsBetter ? 'text-positive' : 'text-danger')
                                : (percentage < 0 ? (d.dataset.tooltip.valuesInverted || d.dataset.tooltip.higherValueIsBetter ? 'text-danger' : 'text-positive') : '')
                        };
                    })
            }
        });
    }

    columns = () => this.currentPeriod.map(
        r => `${this.getLabel(r)}${(r.measurementType ? " (" + this.context.getMeasurementUnit(r.measurementType) + ")" : "")}`);

    getLabel = (r: MeasurementDataset) => {
        const hasIntermediateData: boolean = this.currentPeriod.some(d => d.measurementType.toLowerCase().includes('intermediate'));
        if (hasIntermediateData || !this._splitOffPeak) {
            return r.dataset.label.replace(/\s*\b(?:normal|normaal)\b/i, '').trim();
        }
        return r.dataset.label
    };

    rows = () => Measurements.getSlots(this.filters.timeRange);

    columnTimes = (): ColumnTime[] => {
        const timeRangeForYear: DashboardTime = ChartUtilsService.getForYear(this.filters.timeRange, ChartUtilsService.getComparedYear(this.compare, this.filters.timeRange));
        const previousTimeRange = Measurements.getSlots(timeRangeForYear).map(s => moment(s.startTime).year());
        return Measurements.getSlots(this.filters.timeRange)
            .map((s, i) => (<ColumnTime>{
                previous: previousTimeRange[i],
                current: moment(s.startTime).year()
            }));
    }

    getHeaderTop = () => this.elementRef ? $(this.elementRef.nativeElement).find('#top-header').position()?.top : 0;

    trackBy: any = (index: number, item: any) => item?.label;
}

interface ColumnTime {
    previous: number;
    current: number;
}

interface Data {
    label: string;
    periodChange: boolean;
    data: Record[];
}

interface Record {
    val: number;
    prevVal: number;
    percentage: number;
    finite: boolean;
    cssClass: string;
}